"use client";

import { AnimatePresence, motion } from "motion/react";
import { use, useEffect, useRef } from "react";

import type { PlaceCardBlock } from "~/components/structured-text";
import type { GooglePlace } from "~/lib/google";

import { Context, type PlaceNode } from "./context";

export const useSectionObserver = (placeNode: PlaceNode) => {
  const ref = useRef<HTMLDivElement>(null);
  const { setActive, registerSection, updateSection, unregisterSection } =
    use(Context);

  useEffect(() => {
    const id = placeNode.node.place.id; // Unique identifier for the section
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const y = entry.boundingClientRect.top;
            // If not already registered, register it:
            registerSection(id, y, placeNode);
            // If already registered, update its y position:
            updateSection(id, y);
          } else {
            unregisterSection(id);
          }
        });
      },
      { threshold: 1 },
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [placeNode, registerSection, setActive, unregisterSection, updateSection]);

  return ref;
};

export function TableOfContents() {
  const { placeNodes, active } = use(Context);

  return (
    <motion.ul className="shadow-field relative space-y-2 rounded-xl bg-neutral-200 p-px">
      {placeNodes.map(({ node, googlePlace }) => (
        <MotionTocItem
          key={node.place.id}
          isActive={active?.node.place.id === node.place.id}
          node={node}
          googlePlace={googlePlace}
          href={`#${node.place.id}`}
        />
      ))}
    </motion.ul>
  );
}

function MotionTocItem({
  isActive,
  node,
  googlePlace,
  href,
}: {
  isActive: boolean;
  googlePlace?: GooglePlace;
  node: Omit<PlaceCardBlock, "id" | "__typename">;
  href: string;
}) {
  return (
    <motion.li className="relative block px-4 py-2">
      <a
        href={href}
        className="data-active:text-prune-500 relative z-10 block font-medium"
        data-state={isActive ? "active" : undefined}
      >
        <div>{node.place.title}</div>
        {googlePlace?.formattedAddress ? (
          <div className="text-xs font-normal text-neutral-500">
            {googlePlace.formattedAddress}
          </div>
        ) : null}
      </a>
      <AnimatePresence>
        {isActive ? (
          <motion.div
            layoutId="activeBackground"
            className="absolute inset-0 rounded-xl bg-white"
            transition={{
              type: "spring",
              bounce: 0,
            }}
          />
        ) : null}
      </AnimatePresence>
    </motion.li>
  );
}
